import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatDate } from '../../lib/utils';
import { useFormatting } from '../../hooks/useFormatting';
import { useTerminology } from '../../hooks/useTerminology';
import { ScenarioVersion, ScenarioChange } from '../../types/scenarios';
import { TrendingUp, TrendingDown, Users, Briefcase, CreditCard } from 'lucide-react';

interface VersionViewerModalProps {
  version: ScenarioVersion;
  open: boolean;
  onClose: () => void;
}

export const VersionViewerModal: React.FC<VersionViewerModalProps> = ({
  version,
  open,
  onClose,
}) => {
  const { formatCurrency } = useFormatting();
  const { getScenarioTerm } = useTerminology();
  const versionData = version.data;
  const adjustments = versionData.changes || [];

  // Format a change type string into a human-readable label
  const formatChangeType = (type: string): string => {
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Render the detail line for an adjustment, falling back to description/amount/type
  const renderAdjustmentDetails = (adj: ScenarioChange) => {
    const hasDetailAmount = adj.details && typeof adj.details.amount === 'number';
    const hasDetailPercentage = adj.details && typeof adj.details.percentage === 'number';
    const hasDetailCount = adj.details && typeof adj.details.count === 'number';
    const hasSpecificDetails = hasDetailAmount || hasDetailPercentage || hasDetailCount;

    return (
      <div className="text-muted-foreground">
        {hasSpecificDetails ? (
          <>
            {hasDetailAmount && `${formatCurrency(adj.details.amount as number)}/month`}
            {hasDetailPercentage && ` (${(adj.details.percentage as number) > 0 ? '+' : ''}${adj.details.percentage}%)`}
            {hasDetailCount && `${(adj.details.count as number) > 0 ? '+' : ''}${adj.details.count} people`}
            {hasDetailCount && hasDetailAmount && ` (${formatCurrency(adj.details.amount as number)}/month)`}
          </>
        ) : (
          <>
            {adj.description && <div>{adj.description}</div>}
            {adj.amount !== 0 && <div>{formatCurrency(adj.amount)}</div>}
            {!adj.description && adj.amount === 0 && <div>{formatChangeType(adj.type)}</div>}
          </>
        )}
      </div>
    );
  };

  // Group adjustments by type — using actual ChangeType values
  const revenueAdjustments = adjustments.filter(c => c.type === 'win-customer' || c.type === 'add-revenue' || c.type === 'adjust-revenue' || c.type === 'change-revenue');
  const costAdjustments = adjustments.filter(c => c.type === 'change-costs' || c.type === 'add-cost' || c.type === 'adjust-cost');
  const headcountAdjustments = adjustments.filter(c => c.type === 'hire-employees');
  const projectAdjustments = adjustments.filter(c => c.type === 'capital-purchase');
  const financingAdjustments = adjustments.filter(c => c.type === 'delay-payment' || c.type === 'accelerate-payment');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div>
            <DialogTitle className="text-2xl">
              Version {version.version} (Historical)
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Created by {version.createdByName} on {formatDate(version.createdAt)}
            </p>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Version Description */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">What Changed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{version.description}</p>
            </CardContent>
          </Card>

          {/* Adjustments Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Adjustments in This Version</CardTitle>
            </CardHeader>
            <CardContent>
              {adjustments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No adjustments in this version
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Revenue Adjustments */}
                  {revenueAdjustments.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        <h4 className="font-medium">Revenue ({revenueAdjustments.length})</h4>
                      </div>
                      <div className="space-y-2 ml-6">
                        {revenueAdjustments.map((adj, idx) => (
                          <div key={idx} className="text-sm border-l-2 border-green-200 pl-3 py-1">
                            <div className="font-medium">{adj.name}</div>
                            {renderAdjustmentDetails(adj)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cost Adjustments */}
                  {costAdjustments.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className="h-4 w-4 text-blue-600" />
                        <h4 className="font-medium">Cost ({costAdjustments.length})</h4>
                      </div>
                      <div className="space-y-2 ml-6">
                        {costAdjustments.map((adj, idx) => (
                          <div key={idx} className="text-sm border-l-2 border-blue-200 pl-3 py-1">
                            <div className="font-medium">{adj.name}</div>
                            {renderAdjustmentDetails(adj)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Headcount Adjustments */}
                  {headcountAdjustments.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="h-4 w-4 text-purple-600" />
                        <h4 className="font-medium">Headcount ({headcountAdjustments.length})</h4>
                      </div>
                      <div className="space-y-2 ml-6">
                        {headcountAdjustments.map((adj, idx) => (
                          <div key={idx} className="text-sm border-l-2 border-purple-200 pl-3 py-1">
                            <div className="font-medium">{adj.name}</div>
                            {renderAdjustmentDetails(adj)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Project Adjustments */}
                  {projectAdjustments.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="h-4 w-4 text-orange-600" />
                        <h4 className="font-medium">Projects ({projectAdjustments.length})</h4>
                      </div>
                      <div className="space-y-2 ml-6">
                        {projectAdjustments.map((adj, idx) => (
                          <div key={idx} className="text-sm border-l-2 border-orange-200 pl-3 py-1">
                            <div className="font-medium">{adj.name}</div>
                            {renderAdjustmentDetails(adj)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Financing Adjustments */}
                  {financingAdjustments.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="h-4 w-4 text-pink-600" />
                        <h4 className="font-medium">Financing ({financingAdjustments.length})</h4>
                      </div>
                      <div className="space-y-2 ml-6">
                        {financingAdjustments.map((adj, idx) => (
                          <div key={idx} className="text-sm border-l-2 border-pink-200 pl-3 py-1">
                            <div className="font-medium">{adj.name}</div>
                            {renderAdjustmentDetails(adj)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Scenario Metadata */}
          {versionData.name && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{getScenarioTerm(true)} Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <span className="ml-2 font-medium">{versionData.name}</span>
                  </div>
                  {versionData.category && (
                    <div>
                      <span className="text-muted-foreground">Category:</span>
                      <Badge className="ml-2" variant="outline">
                        {versionData.category}
                      </Badge>
                    </div>
                  )}
                  {versionData.status && (
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <Badge className="ml-2" variant="outline">
                        {versionData.status}
                      </Badge>
                    </div>
                  )}
                  {versionData.createdByName && (
                    <div>
                      <span className="text-muted-foreground">Owner:</span>
                      <span className="ml-2 font-medium">{versionData.createdByName}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Read-Only Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This is a historical version and cannot be edited. 
              To make changes based on this version, use the "Restore" button to create a new version.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
